import clsx from "clsx";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { Asset, MsgExec } from "rujira.js";
import {
  Button,
  Fiat,
  i18n,
  IconDenom,
  nFormatter,
  Numeric,
  useGlobalModalContext,
  Warning,
} from "rujira.ui";
import { ArrowDown, Split } from "rujira.ui/src/components/icons/Icons";
import { MsgProvider, TxButton } from "../../common/components/TxButton";
import { useAccounts } from "../../services/accounts";
import { useMsgAssetFragment } from "../../services/msg";
import { Subscription } from "../../services/useNodeSubscription";
import { PerpsPoolBalanceFragment$key } from "./__generated__/PerpsPoolBalanceFragment.graphql";
import { PerpsPoolBalanceReinvestFragment$key } from "./__generated__/PerpsPoolBalanceReinvestFragment.graphql";
import { PerpsPoolBalanceWithdrawFragment$key } from "./__generated__/PerpsPoolBalanceWithdrawFragment.graphql";

const fragment = graphql`
  fragment PerpsPoolBalanceFragment on PerpsAccount {
    id
    account
    lpSize {
      amount
      asset {
        ...msgAssetFragment
        metadata {
          symbol
        }
      }
      valueUsd
    }
    lpShares
    xlpSize {
      amount
      asset {
        ...msgAssetFragment
        metadata {
          symbol
        }
      }
      valueUsd
    }
    xlpShares
    availableYieldLp {
      amount
      asset {
        asset
        metadata {
          symbol
        }
      }
      valueUsd
    }
    availableYieldXlp {
      amount
      asset {
        asset
      }
      valueUsd
    }
    ...PerpsPoolBalanceReinvestFragment
    ...PerpsPoolBalanceWithdrawFragment
  }
`;

const subscription = graphql`
  subscription PerpsPoolBalanceSubscription($id: ID!) {
    node(id: $id) {
      ... on PerpsAccount {
        id
        lpSize {
          amount
          valueUsd
        }
        lpShares
        xlpSize {
          amount
          valueUsd
        }
        xlpShares
        availableYieldLp {
          amount
          valueUsd
        }
        availableYieldXlp {
          amount
          valueUsd
        }
      }
    }
  }
`;

export const PerpsPoolBalance: FC<{
  address: string;
  k?: PerpsPoolBalanceFragment$key;
}> = ({ address, k }) => {
  const account = useFragment(fragment, k);
  const { showModal, hideModal } = useGlobalModalContext();

  const lpAsset = useMsgAssetFragment(account?.lpSize.asset);

  const withdrawableYield =
    BigInt(account?.availableYieldLp.amount || 0) +
    BigInt(account?.availableYieldXlp.amount || 0);

  return (
    <div className="gradient-card gradient-card--purple h-full p-3 flex dir-c bg-black">
      {account?.id && (
        <Subscription id={account.id} subscription={subscription} />
      )}
      <div className="flex dir-c">
        <i18n.h3 className="fs-16 lh-22 fw-400 color-grey">LP Holdings</i18n.h3>
        <Fiat
          symbol="$"
          amount={BigInt(account?.lpSize.valueUsd || 0)}
          decimals={8}
          className="fs-22 fs-md-32 condensed fw-500 mt-1"
        />
        <Fiat
          symbol={` LP`}
          symbolRight
          amount={BigInt(account?.lpShares || 0)}
          decimals={8}
          className="fs-16 fs-md-18 lh-16 lh-md-18 condensed mt-1 grow"
        />
        {account && (
          <Fiat
            symbol={` ${account.lpSize.asset.metadata.symbol}`}
            symbolRight
            amount={BigInt(account.lpSize.amount || 0)}
            decimals={8}
            className="fs-16 fs-md-18 lh-16 lh-md-18 condensed mt-1 grow"
          />
        )}
      </div>
      <div className="flex dir-c mt-2">
        <i18n.h3 className="fs-16 lh-22 fw-400 color-grey">
          xLP Holdings
        </i18n.h3>
        <Fiat
          symbol="$"
          amount={BigInt(account?.xlpSize.valueUsd || 0)}
          decimals={8}
          className="fs-22 fs-md-32 condensed fw-500 mt-1"
        />
        <Fiat
          symbol={` xLP`}
          symbolRight
          amount={BigInt(account?.xlpShares || 0)}
          decimals={8}
          className="fs-16 fs-md-18 lh-16 lh-md-18 condensed mt-1 grow"
        />
        {account && (
          <Fiat
            symbol={` ${account.xlpSize.asset.metadata.symbol}`}
            symbolRight
            amount={BigInt(account.xlpSize.amount || 0)}
            decimals={8}
            className="fs-16 fs-md-18 lh-16 lh-md-18 condensed mt-1 grow"
          />
        )}
      </div>
      <div className="flex dir-c mt-2">
        <i18n.h3 className="fs-16 lh-22 fw-400 color-grey">
          Withdrawable Yield
        </i18n.h3>
        <Fiat
          symbol="$"
          amount={
            BigInt(account?.availableYieldLp.valueUsd || 0) +
            BigInt(account?.availableYieldXlp.valueUsd || 0)
          }
          decimals={8}
          className="fs-22 fs-md-32 condensed fw-500 mt-1"
        />
        {account && (
          <Fiat
            symbol={` ${account.availableYieldLp.asset.metadata.symbol}`}
            symbolRight
            amount={withdrawableYield}
            decimals={8}
            className="fs-16 fs-md-18 lh-16 lh-md-18 condensed mt-1"
          />
        )}
      </div>
      <Button
        disabled={!lpAsset || withdrawableYield <= 0}
        onClick={() => {
          lpAsset &&
            account &&
            showModal({
              backgroundClose: false,
              children: (
                <Reinvest
                  asset={lpAsset}
                  address={address}
                  k={account}
                  cancel={hideModal}
                />
              ),
            });
        }}
        className="button--outline button--grey mt-3 w-full button--icon-right"
        label="Reinvest Yield">
        <ArrowDown />
      </Button>
      <Button
        disabled={!account?.lpShares || !lpAsset || !account?.lpShares}
        onClick={() => {
          lpAsset &&
            account &&
            showModal({
              backgroundClose: false,
              children: (
                <Withdraw
                  asset={lpAsset}
                  address={address}
                  k={account}
                  cancel={hideModal}
                />
              ),
            });
        }}
        className="button--outline mt-1 w-full button--icon-right"
        label="Withdraw">
        <Split />
      </Button>
    </div>
  );
};

const reinvestFragment = graphql`
  fragment PerpsPoolBalanceReinvestFragment on PerpsAccount {
    id
    availableYieldLp {
      amount
    }
    availableYieldXlp {
      amount
    }
  }
`;

const reinvestSubscription = graphql`
  subscription PerpsPoolBalanceReinvestSubscription($id: ID!) {
    node(id: $id) {
      ...PerpsPoolBalanceReinvestFragment
    }
  }
`;

const Reinvest: React.FC<{
  asset: Asset;
  address: string;
  k: PerpsPoolBalanceReinvestFragment$key;
  cancel: () => void;
}> = ({ asset, address, k, cancel }) => {
  const account = useFragment(reinvestFragment, k);
  const [amount, setAmount] = useState(0n);
  const [type, setType] = useState<"lp" | "xlp">("lp");
  const balance =
    (account?.availableYieldLp.amount || 0n) +
    (account?.availableYieldXlp.amount || 0n);
  const { selected } = useAccounts();
  const msg =
    amount > 0 && selected
      ? new MsgExec(
          { address: selected.address, network: "THOR" },
          asset,
          0n,
          address,
          {
            reinvest_yield: {
              stake_to_xlp: type === "xlp",
              amount: toDecimal(amount),
            },
          }
        )
      : null;

  return (
    <MsgProvider msg={msg}>
      {account?.id && (
        <Subscription id={account.id} subscription={reinvestSubscription} />
      )}

      <h2 className="h3 flex ai-c">
        Reinvest {asset.metadata.symbol}
        <IconDenom
          denom={asset.metadata.symbol}
          className="w-4 h-4 block ml-1"
        />
      </h2>

      <div className="tabs col-8 flex ai-c mt-1">
        <a
          className={clsx({ selected: type === "lp" })}
          onClick={() => setType("lp")}>
          LP
        </a>
        <a
          className={clsx({ selected: type === "xlp" })}
          onClick={() => setType("xlp")}>
          xLP
        </a>
        {type === "xlp" && (
          <p className="fs-14 color-grey ml-2">
            xLP has a 45-day unlock period
          </p>
        )}
      </div>

      <div className="flex ai-c mt-2">
        <i18n.div className="condensed fs-14 color-grey fw-500">
          Amount
        </i18n.div>
        <button
          className="tag tag--borderless px-0.5 pointer ml-a"
          onClick={() => setAmount(balance)}>
          {asset.metadata.symbol} {i18n.t("Balance")}:{" "}
          <span className="color-white ml-0.5">
            {nFormatter(balance, 8, asset.metadata.decimals)}
          </span>
        </button>
      </div>
      <div className="mt-1">
        <Numeric
          full
          decimals={asset.metadata.decimals}
          amount={amount}
          onChangeAmount={setAmount}
        />
      </div>

      <div className="modal__footer mt-4 px-3 py-2 flex ai-s wrap">
        <Button
          className="button--grey button--outline mr-1"
          onClick={cancel}
          label="Cancel"
        />
        <div className="block ml-a text-right">
          <TxButton
            className="button"
            label={`Reinvest in ${type === "lp" ? "LP" : "xLP"}`}
            onSuccess={() => cancel()}
          />
        </div>
      </div>
    </MsgProvider>
  );
};

const withdrawFragment = graphql`
  fragment PerpsPoolBalanceWithdrawFragment on PerpsAccount {
    id
    lpSize {
      amount
    }
    liquidityCooldown {
      endAt
      startAt
    }
  }
`;

const withdrawSubscription = graphql`
  subscription PerpsPoolBalanceWithdrawSubscription($id: ID!) {
    node(id: $id) {
      ...PerpsPoolBalanceWithdrawFragment
    }
  }
`;

const Withdraw: React.FC<{
  asset: Asset;
  address: string;
  k: PerpsPoolBalanceWithdrawFragment$key;
  cancel: () => void;
}> = ({ asset, address, k, cancel }) => {
  const [amount, setAmount] = useState(0n);
  const account = useFragment(withdrawFragment, k);
  const balance = BigInt(account.lpSize.amount);
  const { selected } = useAccounts();
  const msg =
    amount > 0 && selected
      ? new MsgExec(
          { address: selected.address, network: "THOR" },
          asset,
          0n,
          address,
          {
            withdraw_liquidity: {
              lp_amount: toDecimal(amount),
              claim_yield: false,
            },
          }
        )
      : null;

  const cooldown = useCooldown(new Date(account.liquidityCooldown?.endAt));

  return (
    <MsgProvider msg={msg}>
      {account?.id && (
        <Subscription id={account.id} subscription={withdrawSubscription} />
      )}

      <h2 className="h3 flex ai-c">
        {i18n.t("Withdraw")} {asset.metadata.symbol}
        <IconDenom
          denom={asset.metadata.symbol}
          className="w-4 h-4 block ml-1"
        />
      </h2>

      {cooldown !== null && (
        <div className="mb-4">
          <p className="color-grey fs-14">
            Withdrawals are temporarily disabled after depositing.
          </p>
          <Warning
            className="mt-2"
            color="orange"
            msg={`Time remaining: ${cooldown.time}`}
          />
        </div>
      )}

      <div className="flex ai-c">
        <i18n.div className="condensed fs-14 color-grey fw-500">
          Amount
        </i18n.div>
        <button
          className="tag tag--borderless px-0.5 pointer ml-a"
          disabled={cooldown !== null}
          onClick={() => setAmount(balance)}>
          {asset.metadata.symbol} {i18n.t("Balance")}:{" "}
          <span className="color-white ml-0.5">
            {nFormatter(balance, 8, asset.metadata.decimals)}
          </span>
        </button>
      </div>

      <div className="mt-1">
        <Numeric
          full
          decimals={asset.metadata.decimals}
          amount={amount}
          disabled={cooldown !== null}
          onChangeAmount={setAmount}
        />
      </div>

      <div className="modal__footer mt-4 px-3 py-2 flex ai-s wrap">
        <Button
          className="button--grey button--outline mr-1"
          onClick={cancel}
          label="Cancel"
        />
        <div className="block ml-a text-right">
          <TxButton
            disabled={cooldown !== null}
            className="button"
            label="Withdraw"
            onSuccess={() => cancel()}
          />
        </div>
      </div>
    </MsgProvider>
  );
};

const useCooldown = (end: Date) => {
  const [time, setTime] = useState(getTimeRemaining(end));
  const frameRef = useRef<number | null>(null);

  const isCooldown = useCallback(() => {
    return end.getTime() > new Date().getTime();
  }, [end]);

  useEffect(() => {
    const update = () => {
      const newTime = getTimeRemaining(end);
      setTime(newTime);

      if (isCooldown()) {
        frameRef.current = requestAnimationFrame(update);
      }
    };

    if (isCooldown()) {
      frameRef.current = requestAnimationFrame(update);
    }

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [end, isCooldown]);

  if (!isCooldown()) {
    return null;
  }

  return { time };
};

const getTimeRemaining = (endDate: Date) => {
  const now = new Date();
  const diff = endDate.getTime() - now.getTime();

  if (diff <= 0) {
    return "00:00:00";
  }

  const pad = (num: number) => String(num).padStart(2, "0");

  const hours = pad(Math.floor((diff / (1000 * 60 * 60)) % 24));
  const minutes = pad(Math.floor((diff / (1000 * 60)) % 60));
  const seconds = pad(Math.floor((diff / 1000) % 60));

  return `${hours}:${minutes}:${seconds}`;
};

const toDecimal = (value: bigint, decimals = 8): string => {
  const whole = value / 10n ** BigInt(decimals);
  const fraction = (value % 10n ** BigInt(decimals))
    .toString()
    .padStart(decimals, "0");
  return `${whole}.${fraction}`;
};
